import _PictureDialogClearMutation from "../__generated__/PictureDialogClearMutation.graphql";
import _PictureDialogUploadMutation from "../__generated__/PictureDialogUploadMutation.graphql";
import _PictureDialogChooseMutation from "../__generated__/PictureDialogChooseMutation.graphql";
import { clsx } from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { PictureDialogChooseMutation } from '../__generated__/PictureDialogChooseMutation.graphql';
import type { PictureDialogClearMutation } from '../__generated__/PictureDialogClearMutation.graphql';
import type { PictureDialogUploadMutation } from '../__generated__/PictureDialogUploadMutation.graphql';
import { PRESET_ART } from '../avatar/presets';
import { t } from '../i18n/t';
import { pictureView, type Crop } from '../profile/picture';
import { PRESET_SLUGS, presetName, type PresetSlug } from '../profile/presets';
import type { Register } from '../profile/register';
import { blobToBase64, copyForPicture, drawCrop, encodeCrop } from '../profile/upload';
import { codeFromError } from './aidErrors.ts';
import { dragCrop, gridMove, initialCrop, nudgeFor, openingTab, wheelScale, zoomPercent, zoomRange, zoomTo, type PictureTab } from './pictureChoice.ts';

/**
 * `id` beside `picture` so Relay rewrites the learner in place and the
 * transcript and profile card redraw from this one response.
 */
const Choose = (_PictureDialogChooseMutation.hash && _PictureDialogChooseMutation.hash !== "f1467b9bc262a5ba233719856fc1ee2a" && console.error("The definition of 'PictureDialogChooseMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _PictureDialogChooseMutation);
const Upload = (_PictureDialogUploadMutation.hash && _PictureDialogUploadMutation.hash !== "ec1d0b36e51c9637fcfe072adae51153" && console.error("The definition of 'PictureDialogUploadMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _PictureDialogUploadMutation);
const Clear = (_PictureDialogClearMutation.hash && _PictureDialogClearMutation.hash !== "e51806d3e021b80a1c05177035cbd414" && console.error("The definition of 'PictureDialogClearMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _PictureDialogClearMutation);
type Picture = {
  readonly preset?: string | null;
  readonly url?: string | null;
} | null | undefined;

/** `grid-cols-4` below; the roving keys need the same number. */
const COLUMNS = 4;
function PresetGrid({
  current,
  disabled,
  onPick
}: {
  current: PresetSlug | null;
  disabled: boolean;
  onPick: (slug: PresetSlug) => void;
}) {
  const [focus, setFocus] = useState(() => Math.max(0, current ? PRESET_SLUGS.indexOf(current) : 0));
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (e: KeyboardEvent) => {
    const next = gridMove(e.key, focus, PRESET_SLUGS.length, COLUMNS);
    if (next === null) return;
    e.preventDefault();
    setFocus(next);
    buttons.current[next]?.focus();
  };
  return <div role="radiogroup" aria-label={t('picture-tab-presets')} className="grid grid-cols-4 gap-3" onKeyDown={onKeyDown}>
      {PRESET_SLUGS.map((slug, i) => {
      const Art = PRESET_ART[slug];
      const checked = slug === current;
      return <button key={slug} ref={el => {
        buttons.current[i] = el;
      }} type="button" role="radio" aria-checked={checked} aria-label={t(presetName(slug))} title={checked ? t('picture-current') : t(presetName(slug))} tabIndex={i === focus ? 0 : -1} disabled={disabled} onClick={() => onPick(slug)} onFocus={() => setFocus(i)} className={clsx('focus-visible:outline-primary aspect-square rounded-full p-1 focus-visible:outline-2', checked && 'ring-primary ring-4')}>
            <Art className="size-full" />
          </button>;
    })}
    </div>;
}

/**
 * The square the learner frames their face in (PRD §3.2): drag, pinch, wheel
 * or the slider, never a gap. A 512 px backing store drawn at 256 CSS px, so
 * it is sharp on a 2× screen and previews exactly what `encodeCrop` sends.
 */
function CropView({
  bitmap,
  crop,
  onCrop
}: {
  bitmap: ImageBitmap;
  crop: Crop;
  onCrop: (next: (c: Crop) => Crop) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pointers = useRef(new Map<number, {
    x: number;
    y: number;
  }>());
  const {
    width: w,
    height: h
  } = bitmap;
  const {
    min,
    max
  } = zoomRange(w, h);
  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (ctx) drawCrop(ctx, bitmap, crop, 512);
  }, [bitmap, crop]);

  // Not React's `onWheel`, which is passive and cannot stop the dialog
  // scrolling under the zoom.
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      onCrop(c => zoomTo(c, wheelScale(c.scale, e.deltaY), w, h));
    };
    el.addEventListener('wheel', onWheel, {
      passive: false
    });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onCrop, w, h]);
  const viewPx = () => canvas.current?.getBoundingClientRect().width || 256;
  const onPointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY
    });
  };
  const onPointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
    const map = pointers.current;
    const last = map.get(e.pointerId);
    if (!last) return;
    const now = {
      x: e.clientX,
      y: e.clientY
    };
    if (map.size === 1) {
      onCrop(c => dragCrop(c, now.x - last.x, now.y - last.y, viewPx(), w, h));
    } else if (map.size === 2) {
      const other = [...map.entries()].find(([id]) => id !== e.pointerId)![1];
      const before = Math.hypot(last.x - other.x, last.y - other.y);
      const after = Math.hypot(now.x - other.x, now.y - other.y);
      if (before > 0) onCrop(c => zoomTo(c, c.scale * after / before, w, h));
    }
    map.set(e.pointerId, now);
  };
  const onPointerUp = (e: PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const step = nudgeFor(e.key);
    if (!step) return;
    e.preventDefault();
    onCrop(c => dragCrop(c, step[0], step[1], viewPx(), w, h));
  };
  return <div className="flex flex-col items-center gap-3">
      <div className="relative size-64 max-w-full overflow-hidden rounded-box">
        <canvas ref={canvas} width={512} height={512}
      // Not `img`: an image is static to a screen reader, and this one takes
      // the arrow keys. `application` hands them through untranslated.
      role="application" aria-roledescription={t('picture-crop-role')} aria-label={t('picture-crop-label')} tabIndex={0} className="size-full cursor-grab touch-none active:cursor-grabbing" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onKeyDown={onKeyDown} />
        {/* The circle the profile and the transcript will show; the corners
            outside it are still sent, but dimmed so nobody frames for them. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_9999px_rgb(0_0_0/0.45)]" />
      </div>
      <label className="flex w-64 max-w-full flex-col gap-1">
        <span className="fieldset-label">{t('picture-zoom')}</span>
        <input type="range" className="range range-sm" min={min} max={max} step="any" value={crop.scale} aria-valuetext={t('picture-zoom-value', {
        percent: zoomPercent(crop.scale, w, h)
      })} onChange={e => {
        const scale = Number(e.target.value);
        onCrop(c => zoomTo(c, scale, w, h));
      }} />
      </label>
    </div>;
}
function PhotoTab({
  busy,
  onUpload
}: {
  busy: boolean;
  onUpload: (bitmap: ImageBitmap, crop: Crop) => void;
}) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [crop, setCrop] = useState<Crop | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(() => () => bitmap?.close(), [bitmap]);

  // The browser applies EXIF orientation by default (PRD §7.1), and a file it
  // cannot decode is refused here, before anything is sent.
  const open = (file: File | undefined) => {
    if (!file) return;
    setUnreadable(false);
    createImageBitmap(file).then(b => {
      setBitmap(b);
      setCrop(initialCrop(b.width, b.height));
    }, () => setUnreadable(true));
  };
  if (bitmap && crop) {
    return <div className="flex flex-col gap-4">
        <CropView bitmap={bitmap} crop={crop} onCrop={next => setCrop(c => c ? next(c) : c)} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost min-h-(--hit-min)" disabled={busy} onClick={() => {
          setBitmap(null);
          setCrop(null);
        }}>
            {t('common-cancel')}
          </button>
          <button type="button" className="btn btn-primary min-h-(--hit-min)" disabled={busy} onClick={() => onUpload(bitmap, crop)}>
            {busy && <span className="loading loading-spinner loading-sm" aria-hidden="true" />}
            {busy ? t('picture-uploading') : t('picture-use-photo')}
          </button>
        </div>
      </div>;
  }
  return <div className="flex flex-col gap-3">
      <div className={clsx('rounded-box border-base-300 flex flex-col items-center gap-2 border-2 border-dashed p-8', over && 'border-primary bg-primary/5')} onDragOver={e => {
      e.preventDefault();
      setOver(true);
    }} onDragLeave={() => setOver(false)} onDrop={e => {
      e.preventDefault();
      setOver(false);
      open(e.dataTransfer.files[0]);
    }}>
        <label className="btn btn-primary min-h-(--hit-min)">
          {t('picture-choose-file')}
          <input type="file" accept="image/*" className="sr-only" onChange={e => {
          open(e.target.files?.[0]);
          e.target.value = '';
        }} />
        </label>
        <span className="text-base-content/60 text-sm">{t('picture-drop-hint')}</span>
      </div>
      {unreadable && <p role="alert" className="text-error text-sm">
          {t('picture-unreadable')}
        </p>}
    </div>;
}

/**
 * Mounted only while open, so every opening starts on a fresh tab and crop
 * (PRD §3.2). A choice applies the moment it is made, as `DisplayLanguage`'s
 * does, and closes the dialog; a failure keeps it open with the crop intact.
 */
export function PictureDialog({
  picture,
  displayName,
  learnerId,
  register,
  onClose
}: {
  picture: Picture;
  displayName: string;
  learnerId: string;
  register: Register;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const view = pictureView(picture, displayName, learnerId);
  const [tab, setTab] = useState<PictureTab>(() => openingTab(register, view.kind));
  const [error, setError] = useState<string | null>(null);
  const [encoding, setEncoding] = useState(false);
  const [choose, choosing] = useMutation<PictureDialogChooseMutation>(Choose);
  const [upload, uploading] = useMutation<PictureDialogUploadMutation>(Upload);
  const [clear, clearing] = useMutation<PictureDialogClearMutation>(Clear);
  const busy = encoding || choosing || uploading || clearing;

  // Read by the native `close` handler, which runs as a queued task and may
  // see an older render's closure.
  const busyRef = useRef(busy);
  // Set by a completed choice, whose `close` can arrive before the render in
  // which `busy` turns false.
  const finished = useRef(false);
  useLayoutEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  // `closedby="none"` stops Esc and light dismiss outright where the browser
  // knows the attribute; the re-open in `onNativeClose` is the fallback.
  useEffect(() => {
    if (busy) dialog.current?.setAttribute('closedby', 'none');else dialog.current?.removeAttribute('closedby');
  }, [busy]);

  /**
   * Every way out goes through `dialog.close()`, so the browser returns focus
   * to "Change picture" before the parent unmounts this.
   */
  const finish = () => {
    finished.current = true;
    dialog.current?.close();
  };
  const dismiss = () => {
    if (!busy) dialog.current?.close();
  };

  // PRD §3.2: not dismissable by accident mid-choice. Chrome closes a modal
  // without `cancel` on a second Esc with no click between, so a close while
  // busy is undone rather than trusted.
  const onNativeClose = () => {
    if (busyRef.current && !finished.current) {
      dialog.current?.showModal();
      return;
    }
    onClose();
  };
  const pick = (slug: PresetSlug) => {
    if (view.kind === 'preset' && view.slug === slug) return finish();
    setError(null);
    choose({
      variables: {
        preset: slug
      },
      onCompleted: finish,
      onError: () => setError(t('picture-save-failed'))
    });
  };
  const remove = () => {
    setError(null);
    clear({
      variables: {},
      onCompleted: finish,
      onError: () => setError(t('picture-save-failed'))
    });
  };
  const send = async (bitmap: ImageBitmap, crop: Crop) => {
    setError(null);
    setEncoding(true);
    let jpegBase64: string;
    try {
      jpegBase64 = await blobToBase64(await encodeCrop(bitmap, crop));
    } catch {
      setError(t('picture-failed'));
      return;
    } finally {
      setEncoding(false);
    }
    upload({
      variables: {
        jpegBase64
      },
      onCompleted: finish,
      onError: e => setError(t(copyForPicture(codeFromError(e))))
    });
  };
  const hasPicture = Boolean(picture?.preset || picture?.url);
  return <dialog ref={dialog} className="modal" aria-labelledby="picture-dialog-title" onClose={onNativeClose}
  // Esc fires `cancel`; while a choice is in flight it is refused rather
  // than abandoning a request the learner would then not see answered.
  onCancel={e => {
    e.preventDefault();
    dismiss();
  }}>
      <div className="modal-box flex max-w-lg flex-col gap-4">
        <h2 id="picture-dialog-title" className="text-lg font-semibold">
          {t('picture-dialog-title')}
        </h2>

        <div role="tablist" className="tabs tabs-box self-start">
          {(['presets', 'photo'] as const).map(k => <button key={k} type="button" role="tab" id={`picture-tab-${k}`} aria-selected={tab === k} aria-controls="picture-tabpanel" className={clsx('tab', tab === k && 'tab-active')} disabled={busy} onClick={() => {
          setError(null);
          setTab(k);
        }}>
              {t(k === 'presets' ? 'picture-tab-presets' : 'picture-tab-photo')}
            </button>)}
        </div>

        <div role="tabpanel" id="picture-tabpanel" aria-labelledby={`picture-tab-${tab}`}>
          {tab === 'presets' ? <PresetGrid current={view.kind === 'preset' ? view.slug : null} disabled={busy} onPick={pick} /> : <PhotoTab busy={busy} onUpload={(b, c) => void send(b, c)} />}
        </div>

        {error && <div role="alert" className="alert alert-error">
            <span className="text-sm">{error}</span>
          </div>}

        <div className="modal-action mt-0 justify-between">
          {hasPicture ? <button type="button" className="btn btn-ghost text-error min-h-(--hit-min)" disabled={busy} onClick={remove}>
              {t('picture-remove')}
            </button> : <span />}
          <button type="button" className="btn min-h-(--hit-min)" disabled={busy} onClick={dismiss}>
            {t('picture-close')}
          </button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" aria-label={t('picture-close')} tabIndex={-1} disabled={busy} onClick={dismiss} />
    </dialog>;
}